import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const alertVariants = cva(
  "relative w-full rounded-lg border px-3 py-2 text-sm flex items-start gap-2 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0 [&>div]:flex-1 [&>div]:min-w-0",
  {
    variants: {
      variant: {
        default: "bg-muted/40 border-border text-foreground",
        destructive:
          "bg-destructive/10 border-destructive/40 text-foreground [&>svg]:text-destructive",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Alert({
  className,
  variant,
  children,
  ...props
}: Readonly<
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
      data-oid="mt1tr9_"
    >
      {children}
    </div>
  );
}

function AlertTitle({
  className,
  children,
  ...props
}: Readonly<React.HTMLAttributes<HTMLHeadingElement>>) {
  return (
    <h5
      data-slot="alert-title"
      className={cn(
        "font-semibold leading-none tracking-tight mb-2",
        className,
      )}
      {...props}
      data-oid="9qpgzb4"
    >
      {children}
    </h5>
  );
}

function AlertDescription({
  className,
  children,
  ...props
}: Readonly<React.HTMLAttributes<HTMLParagraphElement>>) {
  return (
    <div
      data-slot="alert-description"
      className={cn("text-xs leading-relaxed text-muted-foreground", className)}
      {...props}
      data-oid="nuiit85"
    >
      {children}
    </div>
  );
}

export { Alert, AlertTitle, AlertDescription };
export type { AlertProps };

type AlertProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertVariants>;
